import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, fromEvent, interval, Subject, startWith, takeUntil, throttle, tap, filter, take } from 'rxjs';
import { AuthService } from './auth.service';
import { UserLoginModel } from '../models/user-login.model';
import { UserMessage } from '../shared/error/user-message';
import { ErrorDisplayData } from '../models/error-display-data.model';
import { ConsoleLogger } from '../shared/console-logger';
import { ReminderDialogService } from './reminder-dialog.service';
import { EnvironmentService } from './environment.service';
import { DebugMessage } from '../models/portal-debug-message';

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private ngServiceDestroyUnsubscribe = new Subject<void>();
  private ngIntervalUnsubscribe = new Subject<void>();

  private readonly defaultTimeoutMillis = 300000; // Default time we wait without user activity before timing out
  private readonly reminderTimeoutMillis = 60000;
  private checkForActivityIntervalMillis = 30000; // How often to check for user inactivity
  private debounceTime: number = 5000; // How often to check for user events
  private userSessionExpirationTimeInMillis: number = 0; // Actual date (in ms) when the session will expire

  private click$ = fromEvent<MouseEvent>(window, 'click').pipe(startWith(undefined));
  private mouseMove$ = fromEvent<MouseEvent>(window, 'mousemove').pipe(startWith(undefined));
  private keyPress$ = fromEvent<KeyboardEvent>(window, 'keyup').pipe(startWith(undefined));
  private visibilityChange$ = fromEvent(document, 'visibilitychange');

  constructor(private authService: AuthService, private reminderDialogService: ReminderDialogService, private envService: EnvironmentService)  {
    this.authService.getUserLogin$()
      .pipe(takeUntil(this.ngServiceDestroyUnsubscribe))
      .subscribe((ulm: UserLoginModel) => {
        this.updateUserSessionExpirationTime();
        ConsoleLogger.debug('^^^^ SessionService - authService.getUserLogin$()... user is ' + (ulm ? 'logged in' : 'logged out'));
        if(ulm){
          this.startSessionChecks();
        } else {
          this.stopSessionChecks();
        }
      });
  }

  public ngOnDestroy() {
    this.ngServiceDestroyUnsubscribe.next(undefined);
    this.ngServiceDestroyUnsubscribe.complete();
    this.stopSessionChecks();
  }



  public stopSessionChecks() {
    ConsoleLogger.debug('^^^^ SessionService - stopSessionChecks');
    if (this.ngIntervalUnsubscribe) {
      this.ngIntervalUnsubscribe.next(undefined);
      this.ngIntervalUnsubscribe.complete();
      this.ngIntervalUnsubscribe = new Subject();
    }
    this.userSessionExpirationTimeInMillis = 0;
  }

  public startSessionChecks() {
    ConsoleLogger.debug('^^^^ SessionService - startSessionChecks');
    this.stopSessionChecks(); // stop any existing session info and unsubscribe from all events
    this.updateUserSessionExpirationTime();
    this.startUserActivityMonitor();
    this.startUserSessionTimeoutCheck();
    this.startPageVisibilityMonitor();
  }



  // Periodically check to see if we've encountered user activity
  private startUserSessionTimeoutCheck() {
    ConsoleLogger.debug('^^^^ SessionService - startUserSessionTimeoutCheck');
    interval(this.checkForActivityIntervalMillis)
      .pipe(        
        tap(() => {
          ConsoleLogger.debug('^^^^ SessionService - startUserSessionTimeoutCheck interval check...');
          const user = this.authService.getUserLogin();
          if ( this.getSessionTimeoutMillis() > 120000 && user && !user.isGuestUser() && (this.userSessionExpirationTimeInMillis - this.reminderTimeoutMillis) < Date.now()) {
            this.stopSessionChecks();
            ConsoleLogger.debug('^^^^ SessionService - startUserSessionTimeoutCheck reminderDialogService.open()');
            this.reminderDialogService.open();
          }
        }),
        takeUntil(this.ngIntervalUnsubscribe)
      )
      .subscribe(() => {
        this.checkForExpiredSession();
      });
  }


  // Update the sessionExpirationTime whenever a mouse, keyboard, or click event is encountered
  private startUserActivityMonitor() {
    combineLatest([this.mouseMove$, this.keyPress$, this.click$])
      .pipe(
        throttle(() => interval(this.debounceTime)),
        filter(() => !this.reminderDialogService.isOpen()),
        takeUntil(this.ngIntervalUnsubscribe)
      )
      .subscribe(() => {
        ConsoleLogger.debug('^^^^ SessionService - startUserActivityMonitor...activity detected');
        this.updateUserSessionExpirationTime();
      });
  }

  // Monitor page visibility and check for expired sessions when tab becomes visible
  private startPageVisibilityMonitor() {
    this.visibilityChange$
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntil(this.ngIntervalUnsubscribe)
      )
      .subscribe(() => {
        ConsoleLogger.debug('^^^^ SessionService - startPageVisibilityMonitor...page became visible');
        // Check if session has already expired while the page was hidden
        if (this.userSessionExpirationTimeInMillis > 0 && this.userSessionExpirationTimeInMillis < Date.now()) {
          ConsoleLogger.debug('^^^^ SessionService - startPageVisibilityMonitor...session expired while page was hidden');
          this.checkForExpiredSession();
        }
      });
  }

  private lastActiveApp: string = undefined;
  public updateUserSessionExpirationTime(activeApp?: string) {
    const newExpirationTime = Date.now() + this.getSessionTimeoutMillis();

    this.envService.addDebugMessage(this.authService.getUserLogin()?.getUserGuid(),
      new DebugMessage(undefined, 'Portal', 'Portal', 'logevent',
        'Update session expiration time from: ' + this.userSessionExpirationTimeInMillis + ' to: ' + newExpirationTime,
        this.authService.getDebugParams())
    );

    this.userSessionExpirationTimeInMillis = newExpirationTime;
    this.lastActiveApp = activeApp;
    ConsoleLogger.debug('^^^^ SessionService - updateUserSessionExpirationTime to ' + new Date(this.userSessionExpirationTimeInMillis).toLocaleString());
  }

  private getSessionTimeoutMillis(): number {
    const ulm = this.authService.getUserLogin();
    return ulm ? ulm.getUserSessionTimeoutSeconds() * 1000 : this.defaultTimeoutMillis;
  }


  private checkForExpiredSession() {
    ConsoleLogger.debug('^^^^ SessionService - checkForActivity...checking for expiration at ' + new Date(this.userSessionExpirationTimeInMillis).toLocaleString());
    if (this.userSessionExpirationTimeInMillis < Date.now()) {

      if(this.authService.getUserLogin()?.isGuestUser() && this.envService.getKioskMode()) {
        // If in kiosk mode, just log the guest user back in automatically
        ConsoleLogger.debug('^^^^ SessionService - checkForActivity...expired guest user in kiosk mode, re-logging in');
        this.authService.revokeAndLoginGuest();
        return;
      } else {
        this.authService.logFailInfo(undefined, {fn: 'checkForExpiredSession',
          msg: 'Session expired',
          expirationTime: this.userSessionExpirationTimeInMillis,
          currentTime: Date.now(),
          activeApp: this.lastActiveApp
        }).pipe(take(1)).subscribe();

        ConsoleLogger.debug('^^^^ SessionService - checkForActivity...expired');
        this.authService.logUserInfoToConsole('SESSION EXPIRED');
        this.stopSessionChecks();
        this.reminderDialogService.close();
        this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.sessionExpiredMsg));
      }
    }
  }

  public stopSession_DEVONLY() {
    if (this.envService.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line

    this.userSessionExpirationTimeInMillis = 0;
    this.checkForExpiredSession();
  }

}
